import React, { useRef, useState } from 'react';
import {
  Box,
  Avatar,
  Text,
  Spinner,
  useToast,
  Input,
} from '@chakra-ui/react';
import { uploadProfileImage, getInitials, getAvatarColor } from '../../services/profileImageService';
import { getDefaultAvatarUrl } from '../../utils/defaultAvatar';

interface ProfileAvatarProps {
  userId: string;
  photoURL?: string;
  fullName?: string;
  username?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  editable?: boolean;
  onPhotoUpdate?: (newPhotoURL: string) => void;
}

const sizeMap = {
  sm: { box: '40px', font: 'sm' },
  md: { box: '60px', font: 'md' },
  lg: { box: '80px', font: 'lg' },
  xl: { box: '100px', font: 'xl' },
  '2xl': { box: '120px', font: '2xl' },
};

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  userId,
  photoURL,
  fullName,
  username,
  size = 'xl',
  editable = false,
  onPhotoUpdate,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [localPhotoURL, setLocalPhotoURL] = useState(photoURL);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const initials = getInitials(fullName, username);
  const bgColor = getAvatarColor(userId);
  const dimensions = sizeMap[size];

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please select an image file',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsUploading(true);

    try {
      const downloadURL = await uploadProfileImage(userId, file);
      setLocalPhotoURL(downloadURL);
      
      if (onPhotoUpdate) {
        onPhotoUpdate(downloadURL);
      }

      toast({
        title: 'Photo updated',
        description: 'Your profile picture has been updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Could not upload image',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    if (editable && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <Box position="relative" display="inline-block">
      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        display="none"
        onChange={handleFileSelect}
      />

      {/* Avatar */}
      <Box
        position="relative"
        w={dimensions.box}
        h={dimensions.box}
        borderRadius="full"
        overflow="hidden"
        cursor={editable ? 'pointer' : 'default'}
        onClick={handleClick}
        transition="all 0.2s"
        _hover={editable ? {
          transform: 'scale(1.02)',
          boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)',
        } : {}}
      >
        {localPhotoURL ? (
          <Box
            as="img"
            src={localPhotoURL}
            alt={username || 'Profile'}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        ) : (
          // No uploaded photo - show a deterministic DiceBear cartoon based
          // on the UID. Same identity every render; lobby-wide variety
          // comes from the hash-cycled style. Initials are kept as a
          // last-resort fallback if the DiceBear image fails to load.
          <Box w="100%" h="100%" bg={bgColor} position="relative">
            <Box
              as="img"
              src={getDefaultAvatarUrl(userId)}
              alt={username || 'Profile'}
              w="100%"
              h="100%"
              objectFit="cover"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                // Hide the broken image so the initials underneath show.
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <Box
              position="absolute"
              top={0} left={0} right={0} bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="none"
              zIndex={-1}
            >
              <Text
                fontSize={dimensions.font}
                fontWeight="bold"
                color="white"
                textShadow="0 1px 2px rgba(0,0,0,0.3)"
              >
                {initials}
              </Text>
            </Box>
          </Box>
        )}

        {/* Upload overlay */}
        {editable && !isUploading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            opacity={0}
            transition="opacity 0.2s"
            _groupHover={{ opacity: 1 }}
            sx={{
              '.avatar-container:hover &': { opacity: 1 },
            }}
          >
            <Text color="white" fontSize="xs" fontWeight="bold">
              Change
            </Text>
          </Box>
        )}

        {/* Loading spinner */}
        {isUploading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner color="orange.400" size="md" />
          </Box>
        )}
      </Box>

      {/* Change Picture text for editable mode */}
      {editable && (
        <Text
          textAlign="center"
          color="orange.400"
          fontSize="sm"
          fontWeight="semibold"
          mt={2}
          cursor="pointer"
          onClick={handleClick}
          _hover={{ color: 'orange.300' }}
        >
          Change Picture
        </Text>
      )}
    </Box>
  );
};

export default ProfileAvatar;